import { createSlice } from '@reduxjs/toolkit';
import type { StaffSessionDto, StaffSessionListDto } from '@icebreaker/shared/staff';
import { type ApiErrorBody } from '@icebreaker/shared/errors';
import { createAsyncActions } from '@icebreaker/shared/store';

/**
 * PERSONELİN KENDİ OTURUMLARI (hesap katmanı — şifre değiştirmeyle aynı bölge).
 *
 * Depo oturum sınırında sıfırlanır (`root-reducer`): "tümünü sonlandır"
 * başarılı olduğunda saga `signOut.success` yayınlar, çünkü sunucu bu cihazın
 * oturumunu da düşürmüştür — arayüzün "çıkıldı" demesi olgudur, tahmin değil.
 */
export const fetchStaffSessions = createAsyncActions<void, StaffSessionListDto>(
  'staffSessions/fetch',
);
export const revokeStaffSession = createAsyncActions<{ sessionId: string }, { sessionId: string }>(
  'staffSessions/revoke',
);
export const revokeAllStaffSessions = createAsyncActions<void, void>('staffSessions/revokeAll');

type Phase = 'idle' | 'loading' | 'succeeded' | 'failed';

export interface StaffSessionsState {
  items: StaffSessionDto[];
  phase: Phase;
  error: ApiErrorBody | null;
  /** Tekil ve toplu sonlandırma ortak mutasyon durumu (tek onay penceresi açıktır). */
  mutation: { phase: Phase; error: ApiErrorBody | null };
}

const initialState: StaffSessionsState = {
  items: [],
  phase: 'idle',
  error: null,
  mutation: { phase: 'idle', error: null },
};

const slice = createSlice({
  name: 'staffSessions',
  initialState,
  reducers: {
    sessionsMutationReset(state) {
      state.mutation = { phase: 'idle', error: null };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStaffSessions.request, (state) => {
        state.phase = 'loading';
        state.error = null;
      })
      .addCase(fetchStaffSessions.success, (state, action) => {
        state.phase = 'succeeded';
        state.items = [...action.payload.sessions];
      })
      .addCase(fetchStaffSessions.failure, (state, action) => {
        state.phase = 'failed';
        state.error = action.payload;
      })
      .addCase(revokeStaffSession.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      .addCase(revokeStaffSession.success, (state) => {
        state.mutation.phase = 'succeeded';
      })
      .addCase(revokeStaffSession.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      })
      .addCase(revokeAllStaffSessions.request, (state) => {
        state.mutation = { phase: 'loading', error: null };
      })
      /* `success` buraya düşmez: oturum sınırı depoyu sıfırlar. Hata ise kalır. */
      .addCase(revokeAllStaffSessions.failure, (state, action) => {
        state.mutation = { phase: 'failed', error: action.payload };
      });
  },
});

export const { sessionsMutationReset } = slice.actions;
export const staffSessionsReducer = slice.reducer;
